// ABOUTME: Renders parser operation item rows after applicant CV uploads.
// ABOUTME: Keeps pending parser rows fresh while asynchronous extraction completes.
import Page from "Pages/_Layouts/DefaultInternal";
import { useTranslation } from "react-i18next";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import { navigate } from "@gatsbyjs/reach-router";
import { useAuthState } from "_ReactContext/AuthContext";
import useGetMetadata from "customHooks/useGetMetadata";
import {
    useCompleoSimpleList,
    actionsTableFunction
} from "customHooks/useCompleoSimpleList";
import useActionsTableParserListItem from "./components/useActionsTableParserListItem";
import { Button, Grid } from "@material-ui/core";
import React from "react";
import ModalAutomaticallyProcess from "./ParserComponents/ModalAutomaticallyProcess";
import { useApiCache } from "customHooks/useApi";
import {
    shouldPollParserOperationItems,
    shouldRefreshParserOperationItems
} from "./components/parserItemActions";
import { refreshParserListWhenOperationIsActive } from "./components/parserListRefresh";

const PARSER_LIST_REFRESH_INTERVAL_MS = 3000;
const PARSER_LIST_REFRESH_MAX_ATTEMPTS = 40;

const ParserOperationList = (props: any) => {
    const moduleName = "PARSEROPERATIONLISTDETAIL";
    const [t, i18n, readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });
    const [metadata] = useGetMetadata(moduleName);
    const listAddress = "/parser/parseroperationdetaillist";
    const { company } = useAuthState();
    const [openModalAuto, setOpenModalAuto] = React.useState<{
        open: boolean;
        operationId: string;
        refreshListFunction: () => void;
        itemId?: string;
    }>({
        open: false,
        operationId: "",
        refreshListFunction: () => {},
        itemId: ""
    });
    const actions: actionsTableFunction = useActionsTableParserListItem(
        setOpenModalAuto,
        t
    );
    const postAddressSearch = `/parser/parseroperationsearch`;
    const [parserOperationResult] = useApiCache(
        postAddressSearch,
        "post",
        {
            operationid: props.operationid,
            companyId: company.companyId
        },
        false
    );
    const parserOperationStatus =
        parserOperationResult?.response?.data?.data?.Items?.[0]?.status?.value;
    const [currentOperationStatus, setCurrentOperationStatus] =
        React.useState<string>();
    const showButton = shouldRefreshParserOperationItems(
        currentOperationStatus
    );
    const parserUploadReady =
        props.location?.state?.parserUploadReady === true;
    const shouldPollParserItems = shouldPollParserOperationItems(
        currentOperationStatus,
        parserUploadReady
    );

    const [renderTable, finished, refreshFunction, , finalSearch] =
        useCompleoSimpleList(
            t,
            metadata,
            listAddress,
            "post",
            {
                companyId: company.companyId,
                operationId: props.operationid
                // totalFakeDataMultiply: 650
            },
            actions,
            <Button
                size="small"
                variant="outlined"
                onClick={() => navigate("/applicant/parserlist")}
                color="secondary"
            >
                {t("COMPLEOGENERAL_Back")}
            </Button>
        );
    const refreshAttempts = React.useRef(0);
    const refreshFunctionRef = React.useRef(refreshFunction);

    React.useEffect(() => {
        refreshFunctionRef.current = refreshFunction;
    }, [refreshFunction]);

    React.useEffect(() => {
        setCurrentOperationStatus(parserOperationStatus);
    }, [parserOperationStatus, props.operationid]);

    React.useEffect(() => {
        refreshAttempts.current = 0;

        if (!shouldPollParserItems) {
            return;
        }

        let timeoutId: number | undefined;
        let cancelled = false;

        const scheduleRefresh = () => {
            timeoutId = window.setTimeout(
                refreshParserRows,
                PARSER_LIST_REFRESH_INTERVAL_MS
            );
        };

        const refreshParserRows = async () => {
            if (cancelled) {
                return;
            }

            if (refreshAttempts.current >= PARSER_LIST_REFRESH_MAX_ATTEMPTS) {
                return;
            }

            refreshAttempts.current += 1;
            let shouldKeepRefreshing = false;

            try {
                const result = await refreshParserListWhenOperationIsActive({
                    operationId: props.operationid,
                    companyId: company.companyId,
                    postAddressSearch,
                    refreshList: refreshFunctionRef.current
                });
                shouldKeepRefreshing = result.shouldKeepRefreshing;
                setCurrentOperationStatus(result.currentStatus);
            } catch {
                shouldKeepRefreshing = true;
            } finally {
                if (!cancelled && shouldKeepRefreshing) {
                    scheduleRefresh();
                }
            }
        };

        scheduleRefresh();

        return () => {
            cancelled = true;
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [
        company.companyId,
        postAddressSearch,
        props.operationid,
        shouldPollParserItems
    ]);

    let compRetorno: null | JSX.Element = <Loading />;
    if (readyTranslation && finished) {
        compRetorno = renderTable();
        return (
            <Page
                featureName={t("a_FeatureName")}
                featureMainDescription={t("a_FeatureMainDescription")}
                maxWidth="xl"
                RightButtonComponent={
                    showButton ? (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() =>
                                setOpenModalAuto({
                                    open: true,
                                    operationId: props.operationid,
                                    refreshListFunction: refreshFunction,
                                    itemId: undefined
                                })
                            }
                        >
                            {t("btnProcessAll")}
                        </Button>
                    ) : (
                        <> </>
                    )
                }
            >
                {compRetorno}
                {openModalAuto.open && (
                    <ModalAutomaticallyProcess
                        onClose={() =>
                            setOpenModalAuto({
                                open: false,
                                operationId: "",
                                refreshListFunction: () => {},
                                itemId: undefined
                            })
                        }
                        open={openModalAuto.open}
                        operationId={openModalAuto.operationId}
                        refreshListFunction={openModalAuto.refreshListFunction}
                        operationItemId={openModalAuto.itemId}
                    />
                )}
            </Page>
        );
    } else {
        return <Page>{compRetorno}</Page>;
    }
};

export default ParserOperationList;
